import type { Integration } from '../types/integration'
import type { BankingProviderId } from '../types/jack-henry-banking'
import type { CreditProviderId } from '../types/credit-provider'
import {
  INTEGRATION_PROVIDER_IDS,
  isIntegrationProviderId,
  type IntegrationProviderId,
} from '../types/integration-provider-id'

export type IntegrationProviderSource = Pick<
  Integration,
  | 'integrations'
  | 'bankingProviderId'
  | 'creditProviderIds'
  | 'jackHenrySilverlakeConfig'
  | 'jackHenrySynergyConfig'
  | 'equifaxPrequalConfig'
  | 'equifaxDITConfig'
>

export function normalizeIntegrationProviderIds(
  input: unknown
): IntegrationProviderId[] | undefined {
  if (!Array.isArray(input)) {
    return undefined
  }
  const out: IntegrationProviderId[] = []
  const seen = new Set<string>()
  for (const item of input) {
    if (typeof item !== 'string') {
      continue
    }
    const trimmed = item.trim()
    if (!isIntegrationProviderId(trimmed) || seen.has(trimmed)) {
      continue
    }
    seen.add(trimmed)
    out.push(trimmed)
  }
  return out
}

export function resolveIntegrationProviderIds(
  integration: IntegrationProviderSource
): IntegrationProviderId[] {
  if (integration.integrations?.length) {
    return [...integration.integrations]
  }

  const ids: IntegrationProviderId[] = []
  const seen = new Set<IntegrationProviderId>()

  const add = (id: IntegrationProviderId): void => {
    if (seen.has(id)) {
      return
    }
    seen.add(id)
    ids.push(id)
  }

  if (
    integration.bankingProviderId === 'jack_henry_silverlake' ||
    integration.jackHenrySilverlakeConfig
  ) {
    add('jack_henry_silverlake')
  }
  if (
    integration.bankingProviderId === 'jack_henry_synergy' ||
    integration.jackHenrySynergyConfig
  ) {
    add('jack_henry_synergy')
  }
  if (
    integration.creditProviderIds?.includes('equifax_prequal') ||
    integration.equifaxPrequalConfig
  ) {
    add('equifax_prequal')
  }
  if (
    integration.creditProviderIds?.includes('equifax_dit') ||
    integration.equifaxDITConfig
  ) {
    add('equifax_dit')
  }

  return ids
}

export function integrationHasProvider(
  integration: IntegrationProviderSource,
  providerId: IntegrationProviderId
): boolean {
  return resolveIntegrationProviderIds(integration).includes(providerId)
}

export function integrationProviderIdsFromProductToggles(toggles: {
  equifaxPrequal: boolean
  equifaxDit: boolean
  silverlake: boolean
  synergy: boolean
}): IntegrationProviderId[] {
  const ids: IntegrationProviderId[] = []
  if (toggles.equifaxPrequal) {
    ids.push('equifax_prequal')
  }
  if (toggles.equifaxDit) {
    ids.push('equifax_dit')
  }
  if (toggles.silverlake) {
    ids.push('jack_henry_silverlake')
  }
  if (toggles.synergy) {
    ids.push('jack_henry_synergy')
  }
  return ids
}

export function syncLegacyProviderFieldsFromIntegrations(
  integrations: IntegrationProviderId[]
): {
  bankingProviderId?: BankingProviderId
  creditProviderIds?: CreditProviderId[]
} {
  const creditProviderIds: CreditProviderId[] = []
  if (integrations.includes('equifax_prequal')) {
    creditProviderIds.push('equifax_prequal')
  }
  if (integrations.includes('equifax_dit')) {
    creditProviderIds.push('equifax_dit')
  }

  let bankingProviderId: BankingProviderId | undefined
  if (integrations.includes('jack_henry_silverlake')) {
    bankingProviderId = 'jack_henry_silverlake'
  } else if (integrations.includes('jack_henry_synergy')) {
    bankingProviderId = 'jack_henry_synergy'
  }

  return {
    bankingProviderId,
    creditProviderIds: creditProviderIds.length > 0 ? creditProviderIds : undefined,
  }
}

export function applyIntegrationProviderIdsToIntegration(
  integration: IntegrationProviderSource,
  providerIds: IntegrationProviderId[]
): void {
  integration.integrations = providerIds
  const legacy = syncLegacyProviderFieldsFromIntegrations(providerIds)
  integration.bankingProviderId = legacy.bankingProviderId
  integration.creditProviderIds = legacy.creditProviderIds
}

export { INTEGRATION_PROVIDER_IDS, isIntegrationProviderId }
export type { IntegrationProviderId }
